import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Container,
  CountGroup,
  ActionGroup,
  Button,
  MenuWrap,
  MenuButton,
  MenuList,
  MenuItem,
  Dim,
} from "./styles";
import { useTaskStore } from "store/task";
import { useConfirmStore } from "modal/confirm/store";
import { instanceAPI } from "api";
import Filter from "components/button/filter";

// ---------------- Icons ----------------
const IconPowerOn = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
    <path
      d="M12 2v8M6.35 6.35a8 8 0 1011.3 0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const IconPowerOff = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
    <path
      d="M18 6L6 18M12 2v8"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const IconRestart = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
    <path
      d="M21 12a9 9 0 10-3.3 6.9M21 12h-6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const IconLogout = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
    <path
      d="M10 17l5-5-5-5M15 12H3M21 3v18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const IconDelete = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
    <path
      d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const MenuIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
    <path
      d="M4 6h16M4 12h16M4 18h16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const confirmCopy = {
  on: {
    title: "VDI 전원 켜기",
    desc: "확인 시 선택한 VDI들의 전원이 켜집니다.",
  },
  off: {
    title: "VDI 전원 끄기",
    desc: "확인 시 선택한 VDI들의 전원이 꺼집니다.",
  },
  restart: {
    title: "VDI 재시작",
    desc: "확인 시 선택한 VDI들이 재시작됩니다.",
  },
};

// ---------------- Component ----------------
const ActionPanel = ({
  instanceLen,
  selectInstance,
  filterData,
  setFilterData,
}) => {
  const { addTask } = useTaskStore();
  const { openConfirm } = useConfirmStore();
  const disabled = !selectInstance?.length;
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // ESC / 외부 클릭 시 닫기
  useEffect(() => {
    const onGlobalPointerDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onGlobalPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onGlobalPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const onPowerInstance = useCallback(
    (action: "on" | "off" | "restart") => {
      const { title, desc } = confirmCopy[action];
      openConfirm({
        title,
        desc,
        onConfirm: async () => {
          for (const instance of selectInstance) {
            const res = await instanceAPI.power(instance.name, action);
            addTask({
              name: instance.name,
              id: res.data.task_id,
              job: `VDI Power : ${action}`,
              state: "pending",
            });
          }
        },
      });
    },
    [addTask, openConfirm, selectInstance]
  );

  const onLogout = useCallback(() => {
    openConfirm({
      title: "VDI 로그아웃",
      desc: "확인 시 선택한 VDI들이 로그아웃 됩니다.",
      onConfirm: async () => {
        for (const instance of selectInstance) {
          await instanceAPI.logoff(instance.name);
        }
      },
    });
  }, [openConfirm, selectInstance]);

  const onDelete = useCallback(() => {
    openConfirm({
      title: "VDI 삭제",
      desc: "복구할 수 없습니다. 정말 삭제하시겠습니까?",
      confirmMessage: "삭제하겠습니다",
      onConfirm: async () => {
        for (const instance of selectInstance) {
          const res = await instanceAPI.deleteInstance(instance.name);
          addTask({
            name: instance.name,
            id: res.data.task_id,
            job: `VDI Power : delete`,
            state: "pending",
          });
        }
      },
    });
  }, [addTask, openConfirm, selectInstance]);

  const actions = useMemo(
    () => [
      {
        key: "on",
        label: "Power On",
        variant: "success",
        icon: <IconPowerOn />,
        onClick: () => onPowerInstance("on"),
      },
      {
        key: "off",
        label: "Power Off",
        variant: "warning",
        icon: <IconPowerOff />,
        onClick: () => onPowerInstance("off"),
      },
      {
        key: "restart",
        label: "Restart",
        variant: "info",
        icon: <IconRestart />,
        onClick: () => onPowerInstance("restart"),
      },
      {
        key: "logout",
        label: "Logout",
        variant: "neutral",
        icon: <IconLogout />,
        onClick: onLogout,
      },
      {
        key: "delete",
        label: "Delete",
        variant: "danger",
        icon: <IconDelete />,
        onClick: onDelete,
      },
    ],
    [onPowerInstance, onLogout, onDelete]
  );

  return (
    <Container>
      <CountGroup>
        <span>
          전체: <b>{instanceLen}</b>개
        </span>
        <span>
          선택: <b>{selectInstance.length}</b>개
        </span>
      </CountGroup>

      {/* --- 데스크탑 --- */}
      <ActionGroup className="btn-row">
        {actions.map((a) => (
          <Button
            key={a.key}
            $variant={a.variant}
            disabled={disabled}
            onClick={a.onClick}
          >
            <i>{a.icon}</i>
            {a.label}
          </Button>
        ))}
      </ActionGroup>

      {/* --- 모바일 메뉴 --- */}
      <MenuWrap ref={menuRef}>
        <MenuButton type="button" onClick={() => setOpen((v) => !v)}>
          <i>
            <MenuIcon />
          </i>
        </MenuButton>

        {open && (
          <>
            <MenuList>
              {actions.map((a) => (
                <MenuItem
                  key={a.key}
                  onClick={() => {
                    if (disabled) return;
                    a.onClick();
                    setOpen(false);
                  }}
                  $disabled={disabled}
                >
                  <i>{a.icon}</i>
                  {a.label}
                </MenuItem>
              ))}
            </MenuList>
            <Dim onClick={() => setOpen(false)} />
          </>
        )}
      </MenuWrap>
      <Filter
        tag={Object.keys(filterData)}
        value={filterData}
        onApply={(next) => setFilterData((prev) => ({ ...prev, ...next }))}
      />
    </Container>
  );
};

export default ActionPanel;
